import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { auth, db } from '../firebase';
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  multiFactor,
  TotpMultiFactorGenerator,
  getMultiFactorResolver,
} from 'firebase/auth';
import { collection, query, orderBy, onSnapshot, doc, getDoc } from 'firebase/firestore';
import ProfitCalculator from '../components/ProfitCalculator';

const Admin = () => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [applications, setApplications] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [payingOut, setPayingOut] = useState(null);
  const [activeTab, setActiveTab] = useState('tickets');
  const [processing, setProcessing] = useState(null);

  // 2FA state
  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [showEnroll, setShowEnroll] = useState(false);
  const [totpSecret, setTotpSecret] = useState(null);
  const [totpUri, setTotpUri] = useState('');
  const [enrollCode, setEnrollCode] = useState('');
  const [enrollLoading, setEnrollLoading] = useState(false);
  const [enrollError, setEnrollError] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        const adminDoc = await getDoc(doc(db, 'admins', u.uid));
        setIsAdmin(adminDoc.exists());

        const enrolled = multiFactor(u).enrolledFactors;
        if (adminDoc.exists() && enrolled.length === 0) {
          setShowEnroll(true);
        }
      } else {
        setIsAdmin(false);
        setShowEnroll(false);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!isAdmin) return;

    const unsubApps = onSnapshot(
      query(collection(db, 'affiliate_applications'), orderBy('createdAt', 'desc')),
      (snap) => setApplications(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    const unsubSponsors = onSnapshot(
      query(collection(db, 'sponsor_signups'), orderBy('createdAt', 'desc')),
      (snap) => setSponsors(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    const unsubContacts = onSnapshot(
      query(collection(db, 'contact_submissions'), orderBy('createdAt', 'desc')),
      (snap) => setContacts(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    const unsubTickets = onSnapshot(
      collection(db, 'ticket_purchases'),
      (snap) => setTickets(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error('ticket_purchases listener error:', err)
    );

    const unsubCommissions = onSnapshot(
      collection(db, 'affiliate_commissions'),
      (snap) => setCommissions(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error('affiliate_commissions listener error:', err)
    );

    const unsubAffiliates = onSnapshot(
      collection(db, 'affiliates'),
      (snap) => setAffiliates(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error('affiliates listener error:', err)
    );

    return () => { unsubApps(); unsubSponsors(); unsubContacts(); unsubTickets(); unsubCommissions(); unsubAffiliates(); };
  }, [isAdmin]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError(null);
    try {
      await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
    } catch (err) {
      if (err.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, err);
        setMfaResolver(resolver);
      } else {
        setLoginError('Invalid credentials.');
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setMfaLoading(true);
    setMfaError(null);
    try {
      const totpHint = mfaResolver.hints.find(
        (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID
      );
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(
        totpHint.uid,
        mfaCode
      );
      await mfaResolver.resolveSignIn(assertion);
      setMfaResolver(null);
      setMfaCode('');
    } catch {
      setMfaError('Invalid code. Please try again.');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleEnrollTotp = async () => {
    setEnrollLoading(true);
    setEnrollError(null);
    try {
      const session = await multiFactor(user).getSession();
      const secret = await TotpMultiFactorGenerator.generateSecret(session);
      setTotpSecret(secret);
      setTotpUri(secret.generateQrCodeUrl(user.email, 'Synema Admin'));
    } catch (err) {
      setEnrollError(err.message || 'Failed to generate 2FA secret.');
    } finally {
      setEnrollLoading(false);
    }
  };

  const handleFinishEnroll = async (e) => {
    e.preventDefault();
    setEnrollLoading(true);
    setEnrollError(null);
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
        totpSecret,
        enrollCode
      );
      await multiFactor(user).enroll(assertion, 'Authenticator App');
      setShowEnroll(false);
      setTotpSecret(null);
      setEnrollCode('');
    } catch {
      setEnrollError('Invalid code. Make sure you scanned the QR code and entered the current 6-digit code.');
    } finally {
      setEnrollLoading(false);
    }
  };

  const handleApprove = async (appId) => {
    setProcessing(appId);
    try {
      const idToken = await user.getIdToken();
      const res = await fetch('/.netlify/functions/approve-affiliate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken, applicationId: appId, action: 'approve' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (appId) => {
    if (!window.confirm('Reject this application?')) return;
    setProcessing(appId);
    try {
      const idToken = await user.getIdToken();
      const res = await fetch('/.netlify/functions/approve-affiliate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken, applicationId: appId, action: 'reject' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setProcessing(null);
    }
  };

  const handlePayout = async (affiliateUid) => {
    if (!window.confirm('Send payout to this affiliate?')) return;
    setPayingOut(affiliateUid);
    try {
      const idToken = await user.getIdToken();
      const res = await fetch('/.netlify/functions/payout-affiliate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken, affiliateUid }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      alert(`Payout of $${(data.amount / 100).toFixed(2)} sent successfully!`);
    } catch (err) {
      alert('Payout error: ' + err.message);
    } finally {
      setPayingOut(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 flex items-center justify-center">
        <div className="text-muted text-sm">Loading...</div>
      </div>
    );
  }

  // MFA verification screen (after login, before dashboard)
  if (mfaResolver) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6">

        <div className="max-w-md mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-display text-3xl text-white mb-2 text-center">TWO-FACTOR AUTH</h1>
            <p className="text-[0.85rem] text-muted text-center mb-8">Enter the 6-digit code from your authenticator app.</p>
            <form onSubmit={handleMfaVerify} className="space-y-5">
              {mfaError && (
                <div className="bg-red-500/10 border border-red-500/20 rounded p-3 text-[0.85rem] text-red-400">{mfaError}</div>
              )}
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                required
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                className="w-full bg-dark3 border border-white/[0.06] rounded px-4 py-4 text-2xl text-white text-center font-mono tracking-[0.5em] focus:border-teal/40 focus:outline-none"
                placeholder="000000"
                autoFocus
              />
              <button type="submit" disabled={mfaLoading || mfaCode.length !== 6}
                className={`w-full btn-gradient text-dark font-body text-[0.78rem] font-semibold tracking-[0.08em] uppercase py-3.5 rounded-sm transition-all ${
                  mfaLoading ? 'opacity-60' : 'hover:opacity-90'
                }`}>
                {mfaLoading ? 'Verifying...' : 'Verify'}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    );
  }

  // 2FA enrollment screen (first time after login)
  if (user && isAdmin && showEnroll) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6">

        <div className="max-w-md mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="font-mono text-[0.7rem] tracking-[0.25em] uppercase text-orange mb-4 text-center">
              Security Setup Required
            </div>
            <h1 className="font-display text-3xl text-white mb-2 text-center">SET UP 2FA</h1>
            <p className="text-[0.85rem] text-muted text-center mb-8">
              Protect your admin account with two-factor authentication using an authenticator app (Google Authenticator, Authy, 1Password, etc.).
            </p>

            {!totpSecret ? (
              <div className="text-center">
                {enrollError && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded p-3 text-[0.85rem] text-red-400 mb-4 text-left">{enrollError}</div>
                )}
                <button
                  onClick={handleEnrollTotp}
                  disabled={enrollLoading}
                  className={`btn-gradient text-dark font-body text-[0.78rem] font-semibold tracking-[0.08em] uppercase py-3 px-8 rounded-sm transition-all ${
                    enrollLoading ? 'opacity-60' : 'hover:opacity-90'
                  }`}>
                  {enrollLoading ? 'Generating...' : 'Generate QR Code'}
                </button>
              </div>
            ) : (
              <div>
                <div className="bg-dark2 border border-white/[0.06] rounded p-6 mb-6">
                  <p className="text-[0.8rem] text-muted mb-4 text-center">
                    Scan this QR code with your authenticator app:
                  </p>
                  <div className="flex justify-center mb-4">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(totpUri)}`}
                      alt="2FA QR Code"
                      className="w-48 h-48 rounded"
                    />
                  </div>
                  <p className="text-[0.7rem] text-muted text-center">
                    Can't scan? Enter this key manually:<br />
                    <span className="font-mono text-teal text-[0.75rem] select-all">{totpSecret.secretKey}</span>
                  </p>
                </div>

                <form onSubmit={handleFinishEnroll} className="space-y-4">
                  {enrollError && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded p-3 text-[0.85rem] text-red-400">{enrollError}</div>
                  )}
                  <div>
                    <label className="block font-mono text-[0.6rem] tracking-[0.2em] uppercase text-muted mb-1.5">
                      Enter the 6-digit code from your app
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      required
                      value={enrollCode}
                      onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, ''))}
                      className="w-full bg-dark3 border border-white/[0.06] rounded px-4 py-4 text-2xl text-white text-center font-mono tracking-[0.5em] focus:border-teal/40 focus:outline-none"
                      placeholder="000000"
                    />
                  </div>
                  <button type="submit" disabled={enrollLoading || enrollCode.length !== 6}
                    className={`w-full bg-teal text-dark font-body text-[0.78rem] font-semibold tracking-[0.08em] uppercase py-3.5 rounded-sm transition-all ${
                      enrollLoading ? 'opacity-60' : 'hover:opacity-90'
                    }`}>
                    {enrollLoading ? 'Verifying...' : 'Enable 2FA'}
                  </button>
                </form>
              </div>
            )}

            <button
              onClick={() => { signOut(auth); setShowEnroll(false); }}
              className="w-full text-[0.78rem] text-muted hover:text-white transition-colors mt-6 text-center"
            >
              Log out
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6">

        <div className="max-w-md mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-display text-3xl text-white mb-8 text-center">ADMIN LOGIN</h1>
            <form onSubmit={handleLogin} className="space-y-5">
              {loginError && (
                <div className="bg-red-500/10 border border-red-500/20 rounded p-3 text-[0.85rem] text-red-400">
                  {loginError}
                </div>
              )}
              {user && !isAdmin && (
                <div className="bg-orange/10 border border-orange/20 rounded p-3 text-[0.85rem] text-orange">
                  This account does not have admin access.
                </div>
              )}
              <input
                type="email"
                required
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                className="w-full bg-dark3 border border-white/[0.06] rounded px-4 py-3 text-[0.9rem] text-white/90 font-light focus:border-teal/40 focus:outline-none"
                placeholder="Admin email"
              />
              <input
                type="password"
                required
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                className="w-full bg-dark3 border border-white/[0.06] rounded px-4 py-3 text-[0.9rem] text-white/90 font-light focus:border-teal/40 focus:outline-none"
                placeholder="Password"
              />
              <button
                type="submit"
                disabled={loginLoading}
                className={`w-full btn-gradient text-dark font-body text-[0.78rem] font-semibold tracking-[0.08em] uppercase py-3.5 rounded-sm transition-all ${
                  loginLoading ? 'opacity-60' : 'hover:opacity-90'
                }`}
              >
                {loginLoading ? 'Logging in...' : 'Log In'}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    );
  }

  const pendingApps = applications.filter((a) => a.status === 'pending');
  const approvedApps = applications.filter((a) => a.status === 'approved');
  const rejectedApps = applications.filter((a) => a.status === 'rejected');

  const totalRevenue = tickets.reduce((sum, t) => sum + (t.amountTotal || 0), 0);
  const totalStripeFees = tickets.reduce((sum, t) => sum + (t.stripeFee || 0), 0);
  const totalCommissions = commissions.reduce((sum, c) => sum + (c.commission || 0), 0);
  const totalPaidOut = affiliates.reduce((sum, a) => sum + (a.totalPaid || 0), 0);
  const totalProfit = totalRevenue - totalStripeFees - totalCommissions;
  const directSales = tickets.filter((t) => !t.affiliateCode).length;
  const directRevenue = tickets.filter((t) => !t.affiliateCode).reduce((sum, t) => sum + (t.amountTotal || 0), 0);
  const affiliateRevenue = totalRevenue - directRevenue;

  // Tier breakdown (amounts in cents — accounting for possible 10% affiliate discount)
  const generalTickets = tickets.filter((t) => (t.amountTotal || 0) <= 2500);
  const vipTickets = tickets.filter((t) => (t.amountTotal || 0) > 2500 && (t.amountTotal || 0) <= 5000);
  const allAccessTickets = tickets.filter((t) => (t.amountTotal || 0) > 5000);

  const tabs = [
    { key: 'tickets', label: `Tickets (${tickets.length})` },
    { key: 'payouts', label: `Payouts (${affiliates.length})` },
    { key: 'applications', label: `Affiliates (${pendingApps.length} pending)` },
    { key: 'sponsors', label: `Sponsors (${sponsors.length})` },
    { key: 'contacts', label: `Messages (${contacts.length})` },
    { key: 'calculator', label: 'P&L Calculator' },
  ];

  return (
    <div className="min-h-screen pt-32 pb-20 px-6">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="font-mono text-[0.7rem] tracking-[0.25em] uppercase text-orange mb-2">Admin</div>
            <h1 className="font-display text-3xl text-white">SYNEMA DASHBOARD</h1>
          </div>
          <button onClick={() => signOut(auth)} className="text-[0.78rem] text-muted hover:text-white">
            Log out
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-white/[0.06] pb-4">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`text-[0.8rem] font-medium tracking-wide transition-colors ${
                activeTab === tab.key ? 'text-teal' : 'text-muted hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tickets tab */}
        {activeTab === 'tickets' && (
          <div>
            {/* Metrics */}
            {/* Top-level metrics */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
              {[
                { label: 'Total Sales', value: tickets.length, color: 'text-teal' },
                { label: 'Gross Revenue', value: `$${(totalRevenue / 100).toFixed(2)}`, color: 'text-gold' },
                { label: 'Stripe Fees', value: `$${(totalStripeFees / 100).toFixed(2)}`, color: 'text-[#E05555]' },
                { label: 'Commissions', value: `$${(totalCommissions / 100).toFixed(2)}`, color: 'text-orange' },
                { label: 'Net Profit', value: `$${(totalProfit / 100).toFixed(2)}`, color: totalProfit > 0 ? 'text-teal' : 'text-[#E05555]' },
              ].map((stat) => (
                <div key={stat.label} className="bg-dark2 border border-white/[0.06] rounded p-5">
                  <div className={`font-display text-3xl ${stat.color}`}>{stat.value}</div>
                  <div className="font-mono text-[0.6rem] tracking-[0.15em] uppercase text-muted mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Breakdown */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'Direct Sales', value: directSales, color: 'text-teal' },
                { label: 'Direct Revenue', value: `$${(directRevenue / 100).toFixed(2)}`, color: 'text-teal' },
                { label: 'Affiliate Sales', value: commissions.length, color: 'text-orange' },
                { label: 'Affiliate Revenue', value: `$${(affiliateRevenue / 100).toFixed(2)}`, color: 'text-orange' },
              ].map((stat) => (
                <div key={stat.label} className="bg-dark2 border border-white/[0.04] rounded p-4">
                  <div className={`font-display text-2xl ${stat.color}`}>{stat.value}</div>
                  <div className="font-mono text-[0.55rem] tracking-[0.15em] uppercase text-muted mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Payout summary */}
            <div className="bg-dark2 border border-white/[0.06] rounded p-5 mb-8 flex flex-wrap justify-between items-center gap-4">
              <div className="flex gap-8">
                <div>
                  <div className="text-[0.6rem] font-mono tracking-wider uppercase text-muted">Stripe Fees</div>
                  <div className="font-display text-xl text-[#E05555]">${(totalStripeFees / 100).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-[0.6rem] font-mono tracking-wider uppercase text-muted">Commissions owed</div>
                  <div className="font-display text-xl text-orange">${((totalCommissions - totalPaidOut) / 100).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-[0.6rem] font-mono tracking-wider uppercase text-muted">Already paid out</div>
                  <div className="font-display text-xl text-teal">${(totalPaidOut / 100).toFixed(2)}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[0.6rem] font-mono tracking-wider uppercase text-muted">Margin</div>
                <div className={`font-display text-xl ${totalRevenue > 0 ? 'text-gold' : 'text-muted'}`}>
                  {totalRevenue > 0 ? `${Math.round((totalProfit / totalRevenue) * 100)}%` : '—'}
                </div>
              </div>
            </div>

            {/* Tier breakdown */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              {[
                {
                  label: 'General Pass',
                  price: '$25',
                  count: generalTickets.length,
                  revenue: generalTickets.reduce((s, t) => s + (t.amountTotal || 0), 0),
                  color: 'text-teal',
                  border: 'border-teal/20',
                },
                {
                  label: 'VIP Pass',
                  price: '$50',
                  count: vipTickets.length,
                  revenue: vipTickets.reduce((s, t) => s + (t.amountTotal || 0), 0),
                  color: 'text-orange',
                  border: 'border-orange/20',
                },
                {
                  label: 'All-Access Pass',
                  price: '$75',
                  count: allAccessTickets.length,
                  revenue: allAccessTickets.reduce((s, t) => s + (t.amountTotal || 0), 0),
                  color: 'text-gold',
                  border: 'border-gold/20',
                },
              ].map((tier) => (
                <div key={tier.label} className={`bg-dark2 border ${tier.border} rounded p-5`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className={`font-display text-lg ${tier.color}`}>{tier.label.toUpperCase()}</div>
                      <div className="text-[0.7rem] text-muted">{tier.price} per ticket</div>
                    </div>
                    <div className={`font-display text-3xl ${tier.color}`}>{tier.count}</div>
                  </div>
                  <div className="flex justify-between items-baseline border-t border-white/[0.04] pt-2">
                    <span className="text-[0.7rem] text-muted font-mono uppercase">Revenue</span>
                    <span className="font-mono text-[0.85rem] text-white">${(tier.revenue / 100).toFixed(2)}</span>
                  </div>
                  {tickets.length > 0 && (
                    <div className="flex justify-between items-baseline mt-1">
                      <span className="text-[0.7rem] text-muted font-mono uppercase">Share</span>
                      <span className="font-mono text-[0.75rem] text-muted">
                        {Math.round((tier.count / tickets.length) * 100)}%
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Recent sales */}
            <h2 className="font-display text-xl text-white mb-4">RECENT TICKET SALES</h2>
            {tickets.length === 0 ? (
              <p className="text-[0.85rem] text-muted">No ticket sales yet.</p>
            ) : (
              <div className="space-y-3 mb-12">
                {tickets.map((t) => {
                  const comm = commissions.find((c) => c.sessionId === t.sessionId);
                  const ticketCommission = comm?.commission || 0;
                  const ticketStripeFee = t.stripeFee || 0;
                  const ticketProfit = (t.amountTotal || 0) - ticketStripeFee - ticketCommission;

                  return (
                    <div key={t.id} className="bg-dark2 border border-white/[0.06] rounded p-5 flex justify-between items-center">
                      <div>
                        <div className="text-white/80 text-[0.88rem]">{t.customerName || 'Unknown'}</div>
                        <div className="text-[0.75rem] text-muted">{t.customerEmail}</div>
                      </div>
                      <div className="flex gap-6 items-center">
                        <div className="text-right">
                          <div className="text-[0.65rem] text-[#E05555]">-${(ticketStripeFee / 100).toFixed(2)} stripe</div>
                          {t.affiliateCode && (
                            <div className="text-[0.65rem] text-orange">-${(ticketCommission / 100).toFixed(2)} commission</div>
                          )}
                        </div>
                        {t.affiliateCode ? (
                          <div className="text-[0.7rem] text-orange">Ref: {t.affiliateCode}</div>
                        ) : (
                          <div className="text-[0.7rem] text-teal">Direct</div>
                        )}
                        <div className="text-right min-w-[80px]">
                          <div className="font-mono text-[0.88rem] text-gold">${((t.amountTotal || 0) / 100).toFixed(2)}</div>
                          <div className={`font-mono text-[0.7rem] ${ticketProfit > 0 ? 'text-teal' : 'text-[#E05555]'}`}>
                            net ${(ticketProfit / 100).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Affiliate commissions */}
            {commissions.length > 0 && (
              <>
                <h2 className="font-display text-xl text-white mb-4">AFFILIATE COMMISSIONS</h2>
                <div className="space-y-3">
                  {commissions.map((c) => (
                    <div key={c.id} className="bg-dark2 border border-orange/10 rounded p-5 flex justify-between items-center">
                      <div>
                        <div className="text-white/80 text-[0.88rem] font-mono">{c.affiliateCode}</div>
                        <div className="text-[0.75rem] text-muted">
                          {c.isPartner ? 'Partner (15%)' : 'Standard (10%)'} · {c.customerEmail || 'Unknown'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-[0.88rem] text-orange">${((c.commission || 0) / 100).toFixed(2)}</div>
                        <div className="text-[0.7rem] text-muted">
                          Sale: ${((c.amountPaid || 0) / 100).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Payouts tab */}
        {activeTab === 'payouts' && (
          <div>
            <h2 className="font-display text-xl text-white mb-6">AFFILIATE PAYOUTS</h2>
            {affiliates.length === 0 ? (
              <p className="text-[0.85rem] text-muted">No active affiliates yet.</p>
            ) : (
              <div className="space-y-4">
                {affiliates.map((aff) => {
                  const affCommissions = commissions.filter((c) => c.affiliateCode === aff.promoCode);
                  const totalEarned = affCommissions.reduce((sum, c) => sum + (c.commission || 0), 0);
                  const owed = totalEarned - (aff.totalPaid || 0);

                  return (
                    <div key={aff.id} className="bg-dark2 border border-white/[0.06] rounded p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <div className="text-white font-medium">{aff.name}</div>
                          <div className="text-[0.8rem] text-muted">{aff.email}</div>
                          <div className="flex gap-3 mt-1">
                            <span className="font-mono text-[0.7rem] text-gold">{aff.promoCode}</span>
                            <span className={`text-[0.65rem] uppercase tracking-wider ${
                              aff.tier === 'partner' ? 'text-orange' : 'text-teal'
                            }`}>
                              {aff.tier} · {aff.tier === 'partner' ? '15%' : '10%'}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          {aff.stripeConnectId ? (
                            <span className="text-[0.65rem] uppercase tracking-wider text-teal">Connect active</span>
                          ) : (
                            <span className="text-[0.65rem] uppercase tracking-wider text-muted">No Connect</span>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-3 mb-4">
                        <div className="bg-dark3 rounded p-3">
                          <div className="font-mono text-[0.8rem] text-teal">{affCommissions.length}</div>
                          <div className="text-[0.6rem] text-muted uppercase">Sales</div>
                        </div>
                        <div className="bg-dark3 rounded p-3">
                          <div className="font-mono text-[0.8rem] text-gold">${(totalEarned / 100).toFixed(2)}</div>
                          <div className="text-[0.6rem] text-muted uppercase">Earned</div>
                        </div>
                        <div className="bg-dark3 rounded p-3">
                          <div className="font-mono text-[0.8rem] text-teal">${((aff.totalPaid || 0) / 100).toFixed(2)}</div>
                          <div className="text-[0.6rem] text-muted uppercase">Paid</div>
                        </div>
                        <div className="bg-dark3 rounded p-3">
                          <div className={`font-mono text-[0.8rem] ${owed > 0 ? 'text-orange' : 'text-muted'}`}>
                            ${(owed / 100).toFixed(2)}
                          </div>
                          <div className="text-[0.6rem] text-muted uppercase">Owed</div>
                        </div>
                      </div>

                      {owed > 0 && aff.stripeConnectId && owed >= 2500 && (
                        <button
                          onClick={() => handlePayout(aff.uid)}
                          disabled={payingOut === aff.uid}
                          className={`bg-teal text-dark px-6 py-2 rounded-sm text-[0.75rem] font-semibold uppercase tracking-wider transition-all ${
                            payingOut === aff.uid ? 'opacity-60' : 'hover:opacity-90'
                          }`}
                        >
                          {payingOut === aff.uid ? 'Processing...' : `Pay $${(owed / 100).toFixed(2)}`}
                        </button>
                      )}
                      {owed > 0 && aff.stripeConnectId && owed < 2500 && (
                        <p className="text-[0.75rem] text-muted">Below $25 payout threshold — ${(owed / 100).toFixed(2)} accrued</p>
                      )}
                      {owed > 0 && !aff.stripeConnectId && (
                        <p className="text-[0.75rem] text-muted">Affiliate needs to complete Stripe Connect onboarding before payout.</p>
                      )}
                      {owed <= 0 && (
                        <p className="text-[0.75rem] text-teal">Fully paid up</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Applications tab */}
        {activeTab === 'applications' && (
          <div>
            {/* Pending */}
            <h2 className="font-display text-xl text-white mb-4">PENDING APPLICATIONS</h2>
            {pendingApps.length === 0 ? (
              <p className="text-[0.85rem] text-muted mb-8">No pending applications.</p>
            ) : (
              <div className="space-y-4 mb-12">
                {pendingApps.map((app) => (
                  <div key={app.id} className="bg-dark2 border border-orange/20 rounded p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <div className="text-white font-medium">{app.name}</div>
                        <div className="text-[0.8rem] text-muted">{app.email}</div>
                      </div>
                      <span className={`font-mono text-[0.65rem] tracking-wider uppercase px-2 py-1 rounded ${
                        app.tier === 'partner' ? 'bg-orange/10 text-orange' : 'bg-teal/10 text-teal'
                      }`}>
                        {app.tier}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-[0.8rem] text-white/50 mb-4">
                      <div><span className="text-muted">Platform:</span> {app.platform}</div>
                      <div><span className="text-muted">Audience:</span> {app.audienceSize || 'Not provided'}</div>
                    </div>
                    {app.reason && (
                      <p className="text-[0.8rem] text-white/40 mb-4 italic">"{app.reason}"</p>
                    )}
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleApprove(app.id)}
                        disabled={processing === app.id}
                        className={`bg-teal text-dark px-6 py-2 rounded-sm text-[0.75rem] font-semibold uppercase tracking-wider transition-all ${
                          processing === app.id ? 'opacity-60' : 'hover:opacity-90'
                        }`}
                      >
                        {processing === app.id ? 'Processing...' : 'Approve'}
                      </button>
                      <button
                        onClick={() => handleReject(app.id)}
                        disabled={processing === app.id}
                        className="border border-red-500/30 text-red-400 px-6 py-2 rounded-sm text-[0.75rem] font-semibold uppercase tracking-wider hover:bg-red-500/10 transition-all"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Approved */}
            {approvedApps.length > 0 && (
              <>
                <h2 className="font-display text-xl text-white mb-4">APPROVED ({approvedApps.length})</h2>
                <div className="space-y-3 mb-12">
                  {approvedApps.map((app) => (
                    <div key={app.id} className="bg-dark2 border border-teal/10 rounded p-4 flex justify-between items-center">
                      <div>
                        <span className="text-white/80 text-[0.85rem]">{app.name}</span>
                        <span className="text-muted text-[0.75rem] ml-3">{app.email}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {app.promoCode && (
                          <span className="font-mono text-[0.75rem] text-gold">{app.promoCode}</span>
                        )}
                        <span className="text-teal text-[0.7rem]">Approved</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Rejected */}
            {rejectedApps.length > 0 && (
              <>
                <h2 className="font-display text-xl text-white mb-4">REJECTED ({rejectedApps.length})</h2>
                <div className="space-y-3">
                  {rejectedApps.map((app) => (
                    <div key={app.id} className="bg-dark2 border border-white/[0.04] rounded p-4 flex justify-between items-center opacity-50">
                      <div>
                        <span className="text-white/60 text-[0.85rem]">{app.name}</span>
                        <span className="text-muted text-[0.75rem] ml-3">{app.email}</span>
                      </div>
                      <span className="text-red-400 text-[0.7rem]">Rejected</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Sponsors tab */}
        {activeTab === 'sponsors' && (
          <div className="space-y-4">
            {sponsors.length === 0 ? (
              <p className="text-[0.85rem] text-muted">No sponsor signups yet.</p>
            ) : (
              sponsors.map((s) => (
                <div key={s.id} className="bg-dark2 border border-white/[0.06] rounded p-6">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="text-white font-medium">{s.contactName}</div>
                      <div className="text-[0.8rem] text-muted">{s.email} · {s.phone}</div>
                    </div>
                    <span className="font-mono text-[0.7rem] text-gold">{s.tierName} — {s.tierPrice}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[0.8rem] text-white/50">
                    <div><span className="text-muted">Company:</span> {s.company}</div>
                    <div><span className="text-muted">Industry:</span> {s.industry}</div>
                    <div><span className="text-muted">Video:</span> {s.hasVideoAssets}</div>
                    <div><span className="text-muted">Photo:</span> {s.hasPhotoAssets}</div>
                  </div>
                  {s.notes && <p className="text-[0.8rem] text-white/40 mt-2 italic">"{s.notes}"</p>}
                </div>
              ))
            )}
          </div>
        )}

        {/* Contacts tab */}
        {activeTab === 'contacts' && (
          <div className="space-y-4">
            {contacts.length === 0 ? (
              <p className="text-[0.85rem] text-muted">No messages yet.</p>
            ) : (
              contacts.map((c) => (
                <div key={c.id} className="bg-dark2 border border-white/[0.06] rounded p-6">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="text-white font-medium">{c.name}</div>
                      <div className="text-[0.8rem] text-muted">{c.email}</div>
                    </div>
                    <span className="text-[0.75rem] text-teal">{c.subject}</span>
                  </div>
                  <p className="text-[0.85rem] text-white/50 leading-relaxed">{c.message}</p>
                </div>
              ))
            )}
          </div>
        )}

        {/* Calculator tab */}
        {activeTab === 'calculator' && (
          <ProfitCalculator />
        )}
      </div>
    </div>
  );
};

export default Admin;
